import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que recopilamos',
    body: 'Recopilamos información personal que nos proporcionás voluntariamente al realizar una compra o al contactarnos, como tu nombre, dirección de correo electrónico y datos de pago. No recopilamos información sensible sin tu consentimiento explícito.'
  }, {
    heading: 'Uso de la información',
    body: 'Utilizamos tu información personal para procesar tu compra, enviarte el acceso a la guía digital, y comunicarnos contigo sobre tu pedido. No compartimos tu información con terceros con fines de marketing.'
  }, {
    heading: 'Protección de datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Podés configurar tu navegador para rechazar cookies si lo preferís.'
  }, {
    heading: 'Tus derechos',
    body: 'Tenés derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, podés comunicarte con nosotros a través de los canales disponibles.'
  }, {
    heading: 'Cambios en esta política',
    body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de la última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Aceptación de los términos',
    body: 'Al acceder y utilizar este sitio web y/o adquirir nuestros productos digitales, aceptás estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no utilices este sitio.'
  }, {
    heading: 'Producto digital',
    body: 'SOS Sueño Infantil es una guía digital descargable. Al completar tu compra, recibirás acceso inmediato al material. Dado que se trata de un producto digital, no se aceptan devoluciones una vez entregado el acceso.'
  }, {
    heading: 'Uso del contenido',
    body: 'Todo el contenido de la guía es para uso personal y no comercial. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de responsabilidad',
    body: 'La información proporcionada en la guía es de carácter educativo e informativo. No reemplaza el consejo médico profesional. Siempre consultá con un profesional de la salud antes de realizar cambios en la rutina de sueño de tu hijo/a.'
  }, {
    heading: 'Propiedad intelectual',
    body: 'Todo el contenido de este sitio web y de la guía digital, incluyendo textos, gráficos, logotipos e imágenes, es propiedad de SOS Sueño Infantil y está protegido por las leyes de propiedad intelectual.'
  }, {
    heading: 'Limitación de responsabilidad',
    body: 'SOS Sueño Infantil no se hace responsable por resultados individuales. Los resultados pueden variar según cada familia y situación particular. No garantizamos resultados específicos con el uso de la guía.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos y condiciones en cualquier momento. Las modificaciones entrarán en vigencia desde su publicación en el sitio web.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brown/60 backdrop-blur-sm" onClick={onClose} data-id="element-44">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="bg-cream rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden relative" onClick={e => e.stopPropagation()} data-id="element-45">
        <div className="sticky top-0 bg-cream border-b border-brown/10 px-6 py-5 flex items-center justify-between z-10" data-id="element-46">
          <h2 className="text-2xl font-serif text-brown" data-id="element-47">{content.title}</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-brown/10 transition-colors text-brown/60 hover:text-brown" aria-label="Cerrar" data-id="element-48">
            <XIcon className="w-5 h-5" data-id="element-49" />
          </button>
        </div>

        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-76px)] space-y-6" data-id="element-50">
          {content.sections.map((section, i) => <div key={i} data-id="element-51">
              <h3 className="text-lg font-semibold text-brown mb-2" data-id="element-52">
                {section.heading}
              </h3>
              <p className="text-brown/70 leading-relaxed text-[15px]" data-id="element-53">
                {section.body}
              </p>
            </div>)}

          <p className="text-sm text-brown/40 pt-4 border-t border-brown/10" data-id="element-54">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-AR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brown text-cream/60 py-12 px-6 text-center" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          <p className="mb-6 font-serif text-xl text-cream/80" data-id="element-57">
            SOS Sueño Infantil
          </p>

          <div className="flex justify-center gap-6 mb-6" data-id="element-58">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-cream/50 hover:text-cream transition-colors underline underline-offset-2" data-id="element-59">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-60">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-cream/50 hover:text-cream transition-colors underline underline-offset-2" data-id="element-61">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-xs text-cream/30 max-w-xl mx-auto" data-id="element-62">
            © {new Date().getFullYear()} Todos los derechos reservados. Este
            sitio no es parte del sitio web de Facebook o Facebook Inc. Además,
            este sitio no está respaldado por Facebook de ninguna manera.
            FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-63">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-64" />}
      </AnimatePresence>
    </>;
}